/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Text,
  FormErrorMessage,
} from '@chakra-ui/react';
import DatePicker from 'react-multi-date-picker';
import gregorian_en from 'react-date-object/locales/gregorian_en';
import moment from 'moment';
import ModalComponent from '../Modal';
import InputComponent from '../Input';
import { allowTwoDecimalNumbersInRange, deepClone, validateForm } from '../../utils/helper';
import constants, {
  ADD_FINANCIAL_RULE,
  ADD_FINANCIAL_RULE_DATES,
  weekDayMap,
} from '../../constants';

const getFirstWeekdayOfMarch = (year, weekday) => {
  const date = moment(`${year}/03/01`);
  while (date.day() !== weekday) {
    date.add(1, 'day');
  }
  return date;
};

const FinancialRuleForm = function ({
  isOpen,
  onClose,
  onSave,
  clientWeekDay,
  defaultDate,
  isEdit,
  selectedData,
  selectedIndex,
  rulesExists,
}) {
  const todayDate = moment();
  const { user_details } = useSelector((state) => state.authentication);
  const [userCurrentDate, setUserCurrentDate] = useState();
  const [userEndDate, setUserEndDate] = useState();
  const [validationObject, setValidationObject] = useState(deepClone(ADD_FINANCIAL_RULE));
  const [validationDatesObject, setValidationDatesObject] = useState(
    deepClone(ADD_FINANCIAL_RULE_DATES)
  );
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const calendarRef = useRef();
  const calendarEndRef = useRef();

  const weekTotalOptions = [
    { value: '52', label: '52 Weeks' },
    { value: '53', label: '53 Weeks' },
  ];

  const [formData, setFormData] = useState({
    weekStartDay: '',
    weekStartDate: '',
    selectedWeeks: null,
    weekEndDay: '',
    weekEndDate: '',
    fica: '',
    futa: '',
    suta: '',
    disability: '',
    other: '',
    benefits: '',
    wc: '',
    personnel: '',
    opex_overspend: '',
    financing_cost_overspend: '',
  });
  const resetForm = () => {
    clientWeekDay = null;
    setFormData({
      weekStartDay: '',
      weekStartDate: '',
      selectedWeeks: null,
      weekEndDay: '',
      weekEndDate: '',
      fica: '',
      futa: '',
      suta: '',
      disability: '',
      other: '',
      benefits: '',
      wc: '',
      personnel: '',
      opex_overspend: '',
      financing_cost_overspend: '',
    });
    setValidationObject(deepClone(ADD_FINANCIAL_RULE));
    setValidationDatesObject(deepClone(ADD_FINANCIAL_RULE_DATES));
  };
  useEffect(() => {
    if (clientWeekDay) {
      setFormData((oldData) => ({
        ...oldData,
        weekStartDay: weekDayMap.get(clientWeekDay).display,
      }));

      if (!defaultDate) {
        const year = todayDate.year();
        const date = getFirstWeekdayOfMarch(year, weekDayMap.get(clientWeekDay).idx);
        setUserCurrentDate(date);
      } else {
        let date = moment(defaultDate);
        if (!isEdit) {
          setUserCurrentDate(date.add(1, 'day'));
          setUserEndDate(date.clone().add(6, 'day'));
        } else {
          date = moment(selectedData?.start_date);
          setUserCurrentDate(date);
          setUserEndDate(moment(selectedData?.end_date));
        }
      }
    }
    if (isEdit && selectedData) {
      setFormData((oldData) => ({
        ...oldData,
        selectedWeeks: weekTotalOptions.find(
          (option) => +option.value === selectedData.total_weeks
        ),
        fica: selectedData.fica,
        futa: selectedData.futa,
        suta: selectedData.suta,
        disability: selectedData.disability,
        other: selectedData.other,
        benefits: selectedData.benefits,
        wc: selectedData.wc,
        personnel: selectedData.personnel,
        opex_overspend: selectedData.opex_overspend,
        financing_cost_overspend: selectedData.financing_cost_overspend,
      }));
    }
  }, [isOpen, defaultDate]);

  useEffect(() => {
    if (userCurrentDate) {
      setFormData((oldData) => ({
        ...oldData,
        weekStartDate: moment(userCurrentDate.toDate(), user_details.date_format),
      }));
    }
  }, [userCurrentDate]);

  useEffect(() => {
    if (userEndDate) {
      const diff = moment(userEndDate).diff(moment(userCurrentDate), 'weeks');
      setFormData((oldData) => ({
        ...oldData,
        weekEndDate: userEndDate,
        selectedWeeks: diff + 1,
        weekEndDay: userEndDate.format('dddd'),
      }));
    }
  }, [userEndDate]);

  const handleDateEndChange = (value) => {
    const newData = { weekEndDate: moment(value.toString(), user_details.date_format) };

    // find difference between start date and end date in teams of weeks
    const diff = moment(newData.weekEndDate).diff(moment(formData.weekStartDate), 'weeks');

    if (moment(newData.weekEndDate).isBefore(moment(formData.weekStartDate)) && diff <= 0) {
      newData.weekEndDate = moment(formData.weekStartDate).add(7, 'day');
      newData.selectedWeeks = 0;
    } else {
      newData.selectedWeeks = diff + 1;
    }

    setFormData((oldData) => {
      const newDataForValidation = {
        ...oldData,
        ...newData,
        weekEndDay: newData.weekEndDate.format('dddd'),
      };

      const [validatedDatesObject] = validateForm(newDataForValidation, validationDatesObject);
      setValidationDatesObject(validatedDatesObject);

      return newDataForValidation;
    });
  };

  const handleDateChange = (value) => {
    const newStartDate = moment(value.toString(), user_details.date_format);

    // Create the new data object
    const newData = {
      weekStartDate: newStartDate,
    };

    // Check if the new start date is same as or after the current end date
    if (formData.weekEndDate && newStartDate.isSameOrAfter(formData.weekEndDate)) {
      // If so, set end date to start date + 6 days
      newData.weekEndDate = '';
      newData.selectedWeeks = 0;
    } else {
      newData.selectedWeeks = moment(formData.weekEndDate).diff(newStartDate, 'weeks') + 1;
    }

    // Update form data with the new values
    setFormData((oldData) => ({ ...oldData, ...newData }));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFormSubmit = () => {
    setHasSubmitted(true);
    const [validatedObject, isFormValid] = validateForm(formData, validationObject);
    setValidationObject(validatedObject);

    const [validatedDatesObject, isDatesValid] = validateForm(formData, validationDatesObject);
    setValidationDatesObject(validatedDatesObject);

    setRefreshFlag(!refreshFlag);
    if (isFormValid && isDatesValid) {
      onSave(formData);
      resetForm();
      setHasSubmitted(false);
    }
  };
  const handleInputChange = (field, value) => {
    setFormData((oldData) => {
      const newData = { ...oldData, [field]: value };
      // Only validate if the user has already tried to submit
      if (hasSubmitted) {
        const [validatedObject] = validateForm(newData, validationObject);
        setValidationObject(validatedObject);
      }
      return newData;
    });
  };
  const getForm = () => {
    const isDayDisabled = (date) => date.weekDay.index !== weekDayMap.get(clientWeekDay)?.idx;
    const isEndDayDisabled = (date) => {
      // Get the start day index from the selected start date (not from clientWeekDay)
      const startDate = formData.weekStartDate;
      if (!startDate) return true; // Disable all if no start date

      const startDayIdx = startDate.day(); // moment().day() gives 0 (Sun) to 6 (Sat)
      const endDayIdx = (startDayIdx + 6) % 7; // Day before start day

      const isBeforeStart = startDate && moment(date.toDate()).isBefore(startDate, 'day');
      return date.weekDay.index !== endDayIdx || isBeforeStart;
    };

    return (
      <Grid templateColumns="repeat(4, 1fr)" gap={4} p={4}>
        {isEdit && (
          <GridItem colSpan={4}>
            <Text as="span" fontSize="sm" color="red">
              Note: Editing dates may result in the deletion of the following rule, if applicable.
            </Text>
          </GridItem>
        )}
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              lable="Week Start Day"
              placeholder="Week Start Day"
              value={formData.weekStartDay}
              disabled
              templatevalidation
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl isInvalid={!validationDatesObject.weekStartDate.isvalid} translate="no">
            <FormLabel color="main.primary" fontWeight="normal">
              Start Date
            </FormLabel>
            <DatePicker
              locale={gregorian_en}
              placeholder="Start Date"
              format={user_details.date_format}
              inputClass={
                (rulesExists && !isEdit) || (!!defaultDate && selectedIndex !== 0)
                  ? 'date-picker-disabled'
                  : 'date-picker'
              }
              ref={calendarRef}
              value={
                formData.weekStartDate
                  ? formData.weekStartDate?.format(user_details.date_format)
                  : ''
              }
              weekStartDayIndex={weekDayMap.get(clientWeekDay)?.idx}
              onChange={handleDateChange}
              mapDays={({ date }) => ({ disabled: isDayDisabled(date) })}
              disabled={(rulesExists && !isEdit) || (!!defaultDate && selectedIndex !== 0)}
              editable={false}
            >
              <Button
                bg="main.semiPrimary"
                color="main.white"
                _hover={{ bg: 'main.primary' }}
                _active={{
                  bg: 'main.semiPrimary',
                  borderColor: 'main.primary',
                }}
                onClick={() => calendarRef.current.closeCalendar()}
                size="xs"
                mb={4}
              >
                {constants.DASHBOARD_VIEW.CLOSE_BUTTON_TEXT}
              </Button>
            </DatePicker>
            {!validationDatesObject.weekStartDate.isvalid && (
              <FormErrorMessage>
                {validationDatesObject.weekStartDate.errorMessage}
              </FormErrorMessage>
            )}
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              lable="Week End Day"
              placeholder="Week End Day"
              value={formData.weekEndDay}
              disabled
              templatevalidation
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl isInvalid={!validationDatesObject.weekEndDate.isvalid}>
            <FormLabel color="main.primary" fontWeight="normal">
              End Date
            </FormLabel>
            <DatePicker
              locale={gregorian_en}
              placeholder="End Date"
              format={user_details.date_format}
              inputClass="date-picker"
              ref={calendarEndRef}
              value={
                formData.weekEndDate ? formData.weekEndDate?.format(user_details.date_format) : ''
              }
              weekStartDayIndex={weekDayMap.get(clientWeekDay)?.idx}
              minDate={
                formData.weekStartDate
                  ? formData.weekStartDate.format(user_details.date_format)
                  : undefined
              }
              onChange={handleDateEndChange}
              mapDays={({ date }) => ({ disabled: isEndDayDisabled(date) })}
              editable={false}
            >
              <Button
                bg="main.semiPrimary"
                color="main.white"
                _hover={{ bg: 'main.primary' }}
                _active={{
                  bg: 'main.semiPrimary',
                  borderColor: 'main.primary',
                }}
                onClick={() => calendarEndRef.current.closeCalendar()}
                size="xs"
                mb={4}
              >
                {constants.DASHBOARD_VIEW.CLOSE_BUTTON_TEXT}
              </Button>
            </DatePicker>
            {!validationDatesObject.weekEndDate.isvalid && (
              <FormErrorMessage>{validationDatesObject.weekEndDate.errorMessage}</FormErrorMessage>
            )}
          </FormControl>
        </GridItem>
        <GridItem colSpan={4}>
          <FormControl>
            <InputComponent
              lable="Total Weeks"
              placeholder="Total Weeks"
              labelFontSize="md"
              value={formData.selectedWeeks}
              templatevalidation
              style={{ marginTop: '0.5rem' }}
              disabled
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="FICA"
              placeholder="FICA"
              value={formData.fica}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('fica', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.fica}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="FUTA"
              placeholder="FUTA"
              value={formData.futa}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('futa', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.futa}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="SUTA"
              placeholder="SUTA"
              value={formData.suta}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('suta', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.suta}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="Disability"
              placeholder="Disability"
              value={formData.disability}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('disability', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.disability}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="Other"
              placeholder="Other"
              value={formData.other}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('other', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.other}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="Benefits"
              placeholder="Benefits"
              value={formData.benefits}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('benefits', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.benefits}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="WC"
              placeholder="WC"
              value={formData.wc}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('wc', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.wc}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="Personnel"
              placeholder="Personnel"
              value={formData.personnel}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('personnel', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.personnel}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="OPEX Overspend"
              placeholder="OPEX Overspend"
              value={formData.opex_overspend}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange('opex_overspend', allowTwoDecimalNumbersInRange(value, 0, 100));
              }}
              validationObj={validationObject.opex_overspend}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <InputComponent
              type="number"
              lable="Financing Cost Overspend"
              placeholder="Financing Cost Overspend"
              value={formData.financing_cost_overspend}
              style={{ marginTop: '0.5rem' }}
              labelFontSize="md"
              onChange={(value) => {
                handleInputChange(
                  'financing_cost_overspend',
                  allowTwoDecimalNumbersInRange(value, 0, 100)
                );
              }}
              validationObj={validationObject.financing_cost_overspend}
              refresh={refreshFlag}
            />
          </FormControl>
        </GridItem>
      </Grid>
    );
  };

  return (
    <ModalComponent
      specifySize="xl"
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle={isEdit ? 'Edit Financial Rule' : 'Add Financial Rule'}
      modalContent={getForm()}
      negative={
        <Button onClick={handleClose} bg="main.secondary" mr={3} color="main.black">
          Cancel
        </Button>
      }
      positive={
        <Button
          onClick={handleFormSubmit}
          bg="main.semiPrimary"
          color="main.white"
          _hover={{ bg: 'main.primary' }}
          mr={3}
          _active={{
            bg: 'main.semiPrimary',
            borderColor: 'main.primary',
          }}
        >
          {isEdit ? 'Update' : 'Save'}
        </Button>
      }
    />
  );
};

export default FinancialRuleForm;
